/**
 * Outdoor kitchen builder bridge:
 * capture config + screenshot → create priced Shopify product → add to basket.
 */
(function () {
  "use strict";

  function formatPrice(value, currency) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: currency || "GBP",
        maximumFractionDigits: 0,
      }).format(n);
    } catch (e) {
      return String(Math.round(n));
    }
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function waitForQuote(timeoutMs) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error("Timed out waiting for configuration"));
      }, timeoutMs);

      function onOk(event) {
        const data = event.detail;
        if (data && data.type === "sunnier:ok-quote") {
          cleanup();
          resolve(data.payload || data);
        }
      }
      function onMessage(event) {
        const data = event.data;
        if (data && data.type === "sunnier:ok-quote") {
          cleanup();
          resolve(data.payload || data);
        }
      }
      function cleanup() {
        clearTimeout(timer);
        window.removeEventListener("sunnier:ok", onOk);
        window.removeEventListener("message", onMessage);
      }

      window.addEventListener("sunnier:ok", onOk);
      window.addEventListener("message", onMessage);
      window.dispatchEvent(
        new CustomEvent("sunnier:ok-request-quote", {
          detail: { type: "sunnier:ok-request-quote" },
        }),
      );
    });
  }

  function lineProperties(latest, created) {
    return {
      "Cupboard / module colour": latest?.frontFinishName || "—",
      Worktop: latest?.worktopFinishName || "—",
      Footprint: latest?.footprint || "—",
      Configuration: String(latest?.configuration || latest?.summary || "—").slice(0, 500),
      _ok_config_json: JSON.stringify(latest?.config || {}).slice(0, 9000),
      _ok_product_handle: created?.handle || "",
      _ok_image: created?.featuredImage || "",
    };
  }

  async function createPricedProduct(apiUrl, apiKey, latest) {
    const res = await fetch(apiUrl.replace(/\/$/, ""), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-OK-Cart-Key": apiKey,
      },
      body: JSON.stringify({
        sellIncVat: latest.sellIncVat,
        sellExVat: latest.sellExVat,
        currency: latest.currency || "GBP",
        footprint: latest.footprint,
        summary: latest.summary,
        configuration: latest.configuration,
        frontFinishName: latest.frontFinishName,
        worktopFinishName: latest.worktopFinishName,
        imageDataUrl: latest.imageDataUrl,
        config: latest.config,
        lines: latest.lines,
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      throw new Error(json.error || "Could not create priced product");
    }
    return json;
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function addVariantToCart(variantNumericId, properties) {
    const payload = JSON.stringify({
      items: [{ id: variantNumericId, quantity: 1, properties }],
    });
    const attempts = 6;
    let lastError = new Error("Cart add failed");

    for (let i = 0; i < attempts; i++) {
      const res = await fetch(rootUrl() + "cart/add.js", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: payload,
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) return json;

      const message = json.description || json.message || "Cart add failed";
      lastError = new Error(message);
      const retryable =
        res.status === 422 || /cannot find variant/i.test(String(message));
      if (!retryable || i === attempts - 1) throw lastError;
      await sleep(450 * (i + 1));
    }

    throw lastError;
  }

  function goToBasket() {
    document.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: { open: true },
      }),
    );
    window.location.href = rootUrl() + "cart";
  }

  function boot() {
    const root = document.querySelector("[data-sunnier-ok]");
    if (!root) return;

    const incEl = root.querySelector("[data-sunnier-ok-inc]");
    const ctas = root.querySelectorAll("[data-sunnier-ok-cta]");
    const resetBtn = root.querySelector("[data-sunnier-ok-reset]");
    const saveBtn = root.querySelector("[data-sunnier-ok-save]");
    const overlaysBtn = root.querySelector("[data-sunnier-ok-overlays]");
    const inspectBtn = root.querySelector("[data-sunnier-ok-inspect]");
    const errorEl = root.querySelector("[data-sunnier-ok-error]");
    const fallbackVariantId = Number(root.dataset.variantId);
    const productHandle = (root.dataset.productHandle || "build-outdoor-kitchen").trim();
    const apiUrl = (root.dataset.cartApiUrl || "").trim();
    const apiKey = (root.dataset.cartApiKey || "").trim();

    let latestPrice = null;
    let busy = false;

    function isAdminMode() {
      try {
        return new URLSearchParams(window.location.search).get("admin") === "1";
      } catch (e) {
        return false;
      }
    }

    function requestShareConfigEvent() {
      const detail = { type: "sunnier:ok-request-share-config" };
      window.dispatchEvent(
        new CustomEvent("sunnier:ok-request-share-config", { detail }),
      );
      try {
        window.postMessage(detail, "*");
      } catch (e) {
        /* ignore */
      }
    }

    /** Prefer sync reply (same turn as click) so clipboard keeps user activation. */
    function requestShareConfigSync() {
      let result = null;
      function take(data) {
        if (!data || data.type !== "sunnier:ok-share-config") return;
        result = data;
      }
      function onOk(event) {
        take(event.detail);
      }
      function onMessage(event) {
        take(event.data);
      }
      window.addEventListener("sunnier:ok", onOk);
      window.addEventListener("message", onMessage);
      try {
        requestShareConfigEvent();
      } finally {
        window.removeEventListener("sunnier:ok", onOk);
        window.removeEventListener("message", onMessage);
      }
      return result;
    }

    function waitForShareConfig(timeoutMs) {
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          cleanup();
          reject(new Error("Timed out waiting for configuration"));
        }, timeoutMs);

        function take(data) {
          if (!data || data.type !== "sunnier:ok-share-config") return;
          if (data.error) {
            cleanup();
            reject(new Error(String(data.error)));
            return;
          }
          if (typeof data.cfg === "string" && data.cfg) {
            cleanup();
            resolve(data.cfg);
          }
        }
        function onOk(event) {
          take(event.detail);
        }
        function onMessage(event) {
          take(event.data);
        }
        function cleanup() {
          clearTimeout(timer);
          window.removeEventListener("sunnier:ok", onOk);
          window.removeEventListener("message", onMessage);
        }

        window.addEventListener("sunnier:ok", onOk);
        window.addEventListener("message", onMessage);
        requestShareConfigEvent();
      });
    }

    function copyTextSync(text) {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.setAttribute("aria-hidden", "true");
      // Keep off-screen but large enough that some browsers don't clip selection.
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.width = "2em";
      ta.style.height = "2em";
      ta.style.padding = "0";
      ta.style.border = "none";
      ta.style.outline = "none";
      ta.style.boxShadow = "none";
      ta.style.background = "transparent";
      ta.style.opacity = "0";
      ta.style.zIndex = "-1";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      let ok = false;
      try {
        ok = document.execCommand("copy");
      } catch (e) {
        ok = false;
      }
      // Sanity: selection should cover the full URL (guards against clipped copy).
      if (ok) {
        const selected =
          typeof ta.value === "string" &&
          ta.selectionStart === 0 &&
          ta.selectionEnd === text.length &&
          ta.value.length === text.length;
        if (!selected) ok = false;
      }
      document.body.removeChild(ta);
      return ok;
    }

    async function copyText(text) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (e) {
        /* fall through to execCommand — common after await / denied permission */
      }
      if (copyTextSync(text)) return true;
      return false;
    }

    function promptCopyFallback(text) {
      window.prompt("Copy this full configuration link:", text);
    }

    function showBarError(message) {
      if (!errorEl) return;
      errorEl.hidden = false;
      errorEl.textContent = message || "Something went wrong.";
    }

    function handlePayload(data) {
      if (!data || typeof data !== "object") return;
      if (data.type === "sunnier:ok-cfg-error") {
        showBarError(
          data.message ||
            "This configuration link could not be loaded. It may be incomplete — ask for a fresh Save config link.",
        );
        return;
      }
      if (data.type === "sunnier:ok-price") {
        const currency = data.currency || "GBP";
        const inc = Number(data.sellIncVat);
        if (incEl) incEl.textContent = formatPrice(inc, currency);
        // £0 / empty kitchen — keep Add to basket disabled
        ctas.forEach((cta) => {
          cta.disabled = !Number.isFinite(inc) || inc <= 0;
        });
        latestPrice = { ...(latestPrice || {}), ...data };
      }
    }

    window.addEventListener("sunnier:ok", (event) => handlePayload(event.detail));
    window.addEventListener("message", (event) => handlePayload(event.data));

    if (resetBtn) {
      resetBtn.addEventListener("click", () => {
        window.dispatchEvent(new CustomEvent("sunnier:ok-reset"));
      });
    }

    function emitHideOverlays(hidden) {
      const detail = { type: "sunnier:ok-set-hide-overlays", hidden: !!hidden };
      window.dispatchEvent(
        new CustomEvent("sunnier:ok-set-hide-overlays", { detail }),
      );
      try {
        window.postMessage(detail, "*");
      } catch (e) {
        /* ignore */
      }
    }

    if (overlaysBtn) {
      if (isAdminMode()) {
        overlaysBtn.hidden = false;
        overlaysBtn.removeAttribute("hidden");
      }
      overlaysBtn.addEventListener("click", (event) => {
        event.preventDefault();
        if (!isAdminMode()) return;
        const hidden = overlaysBtn.getAttribute("aria-pressed") !== "true";
        overlaysBtn.setAttribute("aria-pressed", hidden ? "true" : "false");
        overlaysBtn.textContent = hidden ? "Show icons" : "Hide icons";
        overlaysBtn.title = hidden
          ? "Show round 3D icons"
          : "Hide round 3D icons for a clean view";
        root.classList.toggle("sunnier-ok--hide-overlays", hidden);
        emitHideOverlays(hidden);
      });
    }

    function emitInspectMeshes(enabled) {
      const detail = { type: "sunnier:ok-set-inspect-meshes", enabled: !!enabled };
      window.dispatchEvent(
        new CustomEvent("sunnier:ok-set-inspect-meshes", { detail }),
      );
      try {
        window.postMessage(detail, "*");
      } catch (e) {
        /* ignore */
      }
    }

    if (inspectBtn) {
      if (isAdminMode()) {
        inspectBtn.hidden = false;
        inspectBtn.removeAttribute("hidden");
      }
      inspectBtn.addEventListener("click", (event) => {
        event.preventDefault();
        if (!isAdminMode()) return;
        const enabled = inspectBtn.getAttribute("aria-pressed") !== "true";
        inspectBtn.setAttribute("aria-pressed", enabled ? "true" : "false");
        inspectBtn.textContent = enabled ? "Inspecting…" : "Inspect meshes";
        inspectBtn.title = enabled
          ? "Click a 3D part to see its mesh name and height"
          : "Click 3D parts to see mesh names and heights";
        emitInspectMeshes(enabled);
      });
    }

    if (saveBtn) {
      if (isAdminMode()) {
        saveBtn.hidden = false;
        saveBtn.removeAttribute("hidden");
      }
      saveBtn.addEventListener("click", async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isAdminMode()) return;
        if (saveBtn.dataset.busy === "1") return;
        saveBtn.dataset.busy = "1";

        const prev = saveBtn.textContent;
        saveBtn.disabled = true;
        saveBtn.textContent = "Saving…";
        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = "";
        }

        const shareUrlFromCfg = (cfg) => {
          if (typeof cfg !== "string" || !cfg) {
            throw new Error("Empty configuration payload");
          }
          const url = new URL(
            rootUrl() + "products/" + encodeURIComponent(productHandle),
            window.location.origin,
          );
          url.searchParams.set("cfg", cfg);
          // Never include admin=1 on the shareable link.
          url.searchParams.delete("admin");
          const shareUrl = url.toString();
          // Verify the full cfg survived URL serialization (no silent truncation).
          const roundTrip = new URL(shareUrl).searchParams.get("cfg");
          if (roundTrip !== cfg || roundTrip.length !== cfg.length) {
            throw new Error(
              "Share link lost part of the configuration while building the URL",
            );
          }
          return shareUrl;
        };

        const showCopied = () => {
          saveBtn.textContent = "Copied!";
          setTimeout(() => {
            saveBtn.textContent = prev || "Save config";
            saveBtn.disabled = false;
            delete saveBtn.dataset.busy;
          }, 2000);
        };

        const showFailed = (err) => {
          console.error(err);
          saveBtn.textContent = "Failed";
          setTimeout(() => {
            saveBtn.textContent = prev || "Save config";
            saveBtn.disabled = false;
            delete saveBtn.dataset.busy;
          }, 2000);
          showBarError((err && err.message) || "Could not copy share link.");
        };

        try {
          // Sync path: React replies during dispatchEvent → copy while gesture is alive.
          const sync = requestShareConfigSync();
          if (sync && sync.error) {
            throw new Error(String(sync.error));
          }
          let cfg =
            sync && typeof sync.cfg === "string" && sync.cfg ? sync.cfg : null;
          if (!cfg) {
            cfg = await waitForShareConfig(5000);
          }
          const shareUrl = shareUrlFromCfg(cfg);
          console.info(
            "[sunnier-ok] share link ready",
            { cfgLength: cfg.length, urlLength: shareUrl.length },
          );

          // Prefer sync execCommand first (keeps user-gesture); then Clipboard API.
          if (copyTextSync(shareUrl)) {
            showCopied();
            return;
          }
          const copied = await copyText(shareUrl);
          if (copied) {
            showCopied();
            return;
          }
          // Last resort: selectable prompt so the full URL is never lost.
          promptCopyFallback(shareUrl);
          showCopied();
        } catch (err) {
          showFailed(err);
        }
      });
    }

    async function waitForProductImage(handle, maxMs) {
      if (!handle) return "";
      const started = Date.now();
      let delay = 400;
      while (Date.now() - started < maxMs) {
        try {
          const res = await fetch(
            rootUrl() + "products/" + encodeURIComponent(handle) + ".js",
            { headers: { Accept: "application/json" } },
          );
          if (res.ok) {
            const json = await res.json();
            const image = json && json.featured_image;
            if (image) {
              return typeof image === "string"
                ? image
                : image.src || image.url || "";
            }
          }
        } catch (e) {
          /* Shopify attaches the screenshot after the variant is buyable */
        }
        await sleep(delay);
        delay = Math.min(delay + 200, 1000);
      }
      return "";
    }

    async function addConfiguredKitchen(latest) {
      if (!apiUrl || !apiKey) {
        throw new Error(
          "Cart API is not configured. Set Cart API URL and key in the theme section.",
        );
      }
      const created = await createPricedProduct(apiUrl, apiKey, latest);
      const variantId = Number(created.variantNumericId);
      if (!variantId) throw new Error("No variant returned from cart API");
      created.featuredImage = await waitForProductImage(created.handle, 15000);
      await addVariantToCart(variantId, lineProperties(latest, created));
      return created;
    }

    async function onAddClick(event) {
      const btn = event.currentTarget;
      if (busy) return;
      busy = true;
      const prev = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Preparing…";
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = "";
      }

      try {
        const payload = await waitForQuote(10000);
        const merged = { ...(latestPrice || {}), ...payload };
        if (!Number.isFinite(Number(merged.sellIncVat))) {
          throw new Error("Missing price — wait for the kitchen to finish loading.");
        }
        btn.textContent = "Adding…";
        await addConfiguredKitchen(merged);
        btn.textContent = "Added";
        goToBasket();
      } catch (err) {
        console.error(err);
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent =
            err.message || "Could not add to basket. Please try again.";
        }
        btn.disabled = false;
        btn.textContent = prev || "Add to basket";
        busy = false;
      }
    }

    ctas.forEach((cta) => cta.addEventListener("click", onAddClick));

    window.addEventListener("sunnier:ok", async (event) => {
      const data = event.detail;
      if (!data || data.type !== "sunnier:ok-quote" || !data._autoAdd) return;
      if (busy) return;
      busy = true;
      ctas.forEach((cta) => {
        cta.disabled = true;
        cta.textContent = "Adding…";
      });
      try {
        const merged = {
          ...(latestPrice || {}),
          ...(data.payload || data),
        };
        await addConfiguredKitchen(merged);
        goToBasket();
      } catch (err) {
        console.error(err);
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent =
            err.message || "Could not add to basket. Please try again.";
        }
        ctas.forEach((cta) => {
          cta.disabled = false;
          cta.textContent = "Add to basket";
        });
        busy = false;
      }
    });

    // Keep unused lint quiet in older bundlers
    void fallbackVariantId;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
